import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { blue } from '@carbon/colors';
import { Layer, Source } from 'react-map-gl';
import type { CirclePaint, LinePaint } from 'mapbox-gl';
import { circlePaint } from 'components/map/utils';
import { EVAL3_SCOPED_LAYER_IDS } from 'components/map';
import {
  colorRulesAtom,
  inventoryPackageOverridesAtom,
  scopedMapLayersAtom,
  typeCurveOverridesAtom,
  typeCurveScenarioIdAtom,
} from './atoms';
import {
  useCalcLifecycle,
  useColorElements,
  useSlotHighlightFilter,
} from './hooks';
import { buildColorExpression, packageMapSourceUrl } from './utils';

/**
 * Slot geometry color: the mock UI represents slots with `blue[60]` (#0f62fe) —
 * the slot swatch in the Selection Bar legend (`MasterDataPanel.tsx`). (The mock
 * map draws resting sticks near-black and only turns them this blue on highlight,
 * but we have no selection/highlight feature yet, so slots use the legend's blue
 * directly.) All well geometries (stick / survey / producing segment / hole
 * points) share it at 0.75px so the layer reads as one. It's also the
 * color-by-attribute default — features matching no color rule keep it.
 */
const WELL_COLOR = blue[60];
const WELL_LINE_WIDTH = 0.75;
const HOLE_PAINT = circlePaint(WELL_COLOR);

interface SlotLayerProps {
  /** The package whose scoped well tiles are rendered (from `PackageMap`). */
  packageId: number;
  /** The eval-calc id (calc mode) or null — threaded into color/visibility resolution. */
  calculationId: number | null;
}

/**
 * Package-scoped Slot map layer — the `wells_v2` MVT tile, which emits one MVT
 * layer per well geometry (there is no centroid): the stick, survey, and
 * estimated-producing-segment lines plus the surface-hole and bottom-hole points.
 * The three lateral LINES follow a descending hierarchy — exactly one draws per
 * well, preferring survey > stick > estimated-producing-segment (see the
 * `stickFilter` / `epsFilter` gates below; INS-10376 Bug 7) — while the hole points
 * always draw.
 * The tile is `minzoom 10`, so features appear only once zoomed in. Visibility is driven by the `slots` key of `scopedMapLayersAtom` via
 * Mapbox `layout.visibility`, so the source stays mounted and toggling never
 * refetches. When a row is pinned, the layer is additionally filtered to that
 * row's related wells (by `uwi`); nothing pinned → `['all']` → all wells draw.
 *
 * The lines + points are colored by the wells dataset's color rules: the enabled,
 * map-targeting rules resolve to ids (`useColorElements`) that paint matching wells
 * (by `uwi`) their rule color via a Mapbox `match`, falling back to `WELL_COLOR`.
 *
 * @param {SlotLayerProps} props - The package id + eval-calc id the tiles are scoped to.
 * @return {JSX.Element} The well tile source with its line + point layers.
 */
export function SlotLayer({ packageId, calculationId }: SlotLayerProps) {
  const { slots } = useAtomValue(scopedMapLayersAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  // The manager folds ride the source URL because the filterSet token the sources endpoint
  // mints is hashed over them (FilterSet::Token). Omitting them resolved this layer's token
  // against the DEFAULT curves while the LTD layer used the overridden ones, so the two
  // layers disagreed about which places exist whenever overrides were pending (PR #7365
  // review). Same two atoms the LTD layer reads.
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const layout = { visibility: slots ? 'visible' : 'none' } as const;
  // Layer filters (memoized in the hook — that stability is load-bearing for smooth
  // zoom; see its comment). `filter` (base) drives the survey line + the two hole
  // points; `stickFilter` / `epsFilter` gate it to the survey > stick > EPS lateral
  // hierarchy (see `slotLateralFilters`). Always valid arrays, never undefined, or
  // mapbox's addLayer validation rejects the layer.
  const {
    base: filter,
    eps: epsFilter,
    stick: stickFilter,
  } = useSlotHighlightFilter(packageId, calculationId);

  // Color-by-attribute (wells): this layer's enabled, map-targeting color rules →
  // resolved ids → a Mapbox `match` over the well `uwi`, falling back to the flat
  // WELL_COLOR when nothing matches / no rules. The paint memos use the same
  // load-bearing discipline as the highlight filter — react-map-gl re-applies paint
  // on a fresh object identity and janks zoom otherwise; `colorElements` is
  // referentially stable (RQ signature memo), so these recompute only on a real
  // color change (recoloring repaints; it only refetches if it re-partitions which
  // values share a color).
  const colorRules = useAtomValue(colorRulesAtom('wells'));
  const mapRules = useMemo(
    () =>
      colorRules.filter((rule) => rule.enabled && rule.applyTo.includes('map')),
    [colorRules]
  );
  const colorElements = useColorElements(packageId, calculationId, mapRules);

  // Calc-scoped tiles exist only for COMPLETED calcs (P5 rule) — a URL has no RQ
  // `enabled`, so gate the id itself: package tiles while the calc is Processing,
  // calc-mode tiles once its detail reads Completed.
  const { canFetch: calcTilesReady } = useCalcLifecycle(calculationId);
  const colorByUwi = useMemo(
    () => buildColorExpression(colorElements, 'uwi', WELL_COLOR),
    [colorElements]
  );
  const linePaint = useMemo<LinePaint>(
    () => ({ 'line-color': colorByUwi, 'line-width': WELL_LINE_WIDTH }),
    [colorByUwi]
  );
  const holePaint = useMemo<CirclePaint>(
    () => ({ ...HOLE_PAINT, 'circle-color': colorByUwi }),
    [colorByUwi]
  );

  return (
    <Source
      id="eval3-slots"
      type="vector"
      // The scenario keys this source's filterSet token (INS-10669): its members include the
      // `slot` grain, which holds the Type Curve LTD place ids. Omitting it here would resolve a
      // Legacy-keyed token while the LTD layer used the picked one, and the two layers would
      // then disagree about which places exist.
      url={packageMapSourceUrl(
        packageId,
        'wells_v2',
        calcTilesReady ? calculationId : null,
        typeCurveScenarioId,
        typeCurveOverrides,
        inventoryPackageOverrides
      )}
    >
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.slot.stick}
        type="line"
        source-layer="stick"
        paint={linePaint}
        layout={layout}
        filter={stickFilter}
      />
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.slot.survey}
        type="line"
        source-layer="survey"
        paint={linePaint}
        layout={layout}
        filter={filter}
      />
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.slot.eps}
        type="line"
        source-layer="estimated-producing-segment"
        paint={linePaint}
        layout={layout}
        filter={epsFilter}
      />
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.slot.surfaceHole}
        type="circle"
        source-layer="surface-hole"
        paint={holePaint}
        layout={layout}
        filter={filter}
      />
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.slot.bottomHole}
        type="circle"
        source-layer="bottom-hole"
        paint={holePaint}
        layout={layout}
        filter={filter}
      />
    </Source>
  );
}
